import { IFavoriteCountsSchema } from '../repositories/favorites/entities/favorite_counts/FavoriteCountsSchema';
import { EDealType } from '../repositories/favorites/entities/favorite_entity_for_desktop/FavoriteEntityForDesktopSchema';
import { EEntityType, IFiltersState } from '../types/filters';

export function getCountsForDealType(counts: IFavoriteCountsSchema, dealType: EDealType | null): number | null {
  if (!dealType) {
    return counts.total - (counts.sale.newbuilding || 0);
  }

  if (dealType === EDealType.Rent && counts.rent) {
    return Object.keys(counts.rent).reduce((acc, key: keyof typeof counts.rent) => {
      return acc + (counts.rent[key] || 0);
    }, 0);
  }

  if (dealType === EDealType.Sale && counts.sale) {
    return Object.keys(counts.sale).reduce((acc, key: keyof typeof counts.sale) => {
      return acc + (counts.sale[key] || 0);
    }, 0);
  }

  return null;
}

export function getCountsForEntityType(
  counts: IFavoriteCountsSchema,
  dealType: EDealType | null,
  entityType: EEntityType | null,
): number {
  const DEFAULT_VALUE = 0;

  const getValue = (counts: IFavoriteCountsSchema, key: keyof typeof counts.rent) => {
    const valueRent = counts.rent ? counts.rent[key] : DEFAULT_VALUE;
    const valueSale = counts.sale ? counts.sale[key] : DEFAULT_VALUE;

    if (valueRent && valueSale) {
      return valueRent + valueSale;
    }

    if (valueRent) {
      return valueRent;
    }

    if (valueSale) {
      return valueSale;
    }

    return DEFAULT_VALUE;
  };

  if (!dealType) {
    switch (entityType) {
      case EEntityType.Residential:
        return getValue(counts, 'offerResidental');
      case EEntityType.Suburban:
        return getValue(counts, 'offerSuburban');
      case EEntityType.Commercial:
        return getValue(counts, 'offerCommercial');
      case EEntityType.Newbuilding:
        return getValue(counts, 'newbuilding');
      default:
        return DEFAULT_VALUE;
    }
  }

  if (dealType === EDealType.Rent && counts.rent) {
    switch (entityType) {
      case EEntityType.Residential:
        return counts.rent.offerResidental || DEFAULT_VALUE;
      case EEntityType.Suburban:
        return counts.rent.offerSuburban || DEFAULT_VALUE;
      case EEntityType.Commercial:
        return counts.rent.offerCommercial || DEFAULT_VALUE;
      case EEntityType.Newbuilding:
        return counts.rent.newbuilding || DEFAULT_VALUE;
      default:
        return DEFAULT_VALUE;
    }
  }

  if (dealType === EDealType.Sale && counts.sale) {
    switch (entityType) {
      case EEntityType.Residential:
        return counts.sale.offerResidental || DEFAULT_VALUE;
      case EEntityType.Suburban:
        return counts.sale.offerSuburban || DEFAULT_VALUE;
      case EEntityType.Commercial:
        return counts.sale.offerCommercial || DEFAULT_VALUE;
      case EEntityType.Newbuilding:
        return counts.sale.newbuilding || DEFAULT_VALUE;
      default:
        return DEFAULT_VALUE;
    }
  }

  return DEFAULT_VALUE;
}

/**
 * Возвращает ссылку для редиректа на страницу поиска,
 * по умолчанию если не выбран конкретный тип недвижимости - редиректим на "купить квартиру",
 * если не выбран тип сделки, то по умолчанию считаем что это "купить"
 *
 * @param filters - фильтры с dealType и favoriteEntityType
 */
export function getSearchUrl(filters: IFiltersState): string {
  const { dealType: filterDealType, favoriteEntityType } = filters;

  const defaultDealType = 'kupit';

  const dealType = filterDealType ? (filterDealType === EDealType.Rent ? 'snyat' : 'kupit') : defaultDealType;

  switch (favoriteEntityType) {
    case EEntityType.Residential:
      return `/${dealType}-kvartiru/`;
    case EEntityType.Suburban:
      return `/${dealType}-dom/`;
    case EEntityType.Commercial:
      return `/${dealType}-ofis/`;
    case EEntityType.Newbuilding:
      return `/novostroyki/`;
    default:
      return `/${dealType}-kvartiru/`;
  }
}
